import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import api from '../../services/api';

interface JurisprudenceResult {
  id: string;
  titulo: string;
  tribunal: string;
  numero_processo: string;
  ementa: string;
  inteiro_teor?: string;
  relevancia?: number;
}

interface SimplifiedJurisprudencePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onInsertJurisprudence?: (jurisprudence: string) => void;
}

const SimplifiedJurisprudencePanel: React.FC<SimplifiedJurisprudencePanelProps> = ({
  isOpen,
  onClose,
  onInsertJurisprudence
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<JurisprudenceResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchJurisprudence = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/jurisprudence/search', {
        params: {
          query: searchQuery,
          size: 10
        }
      });

      if (response.data.status === 'success' && response.data.data) {
        const convertedResults: JurisprudenceResult[] = response.data.data.map((item: any) => ({
          id: item.id || Math.random().toString(),
          titulo: item.titulo || item.ementa?.substring(0, 100) + '...' || 'Sem título',
          tribunal: item.tribunal || 'Tribunal não informado',
          numero_processo: item.numero_processo || 'Não informado',
          ementa: item.ementa || 'Ementa não disponível',
          inteiro_teor: item.inteiro_teor,
          relevancia: item.relevancia || 0
        }));
        setResults(convertedResults);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error('Erro na busca de jurisprudência:', err);
      setError('Erro ao buscar jurisprudência. Tente novamente.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const insertJurisprudence = (result: JurisprudenceResult) => {
    const citation = `
**${result.tribunal}**
Processo: ${result.numero_processo}

*Ementa:*
"${result.ementa}"

---
`;

    if (onInsertJurisprudence) {
      onInsertJurisprudence(citation);
    }
  };

  if (!isOpen) return null;

  return (
    <Paper
      sx={{
        position: 'fixed',
        top: 64,
        right: 0,
        width: 450,
        height: 'calc(100vh - 64px)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        borderLeft: 1,
        borderColor: 'divider'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: '#f8f9fa'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon color="primary" />
            <Typography variant="h6">
              Jurisprudência
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Busca */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar jurisprudência..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && searchJurisprudence()}
          InputProps={{
            endAdornment: (
              <IconButton 
                size="small" 
                onClick={searchJurisprudence}
                disabled={loading || !searchQuery.trim()}
              >
                {loading ? <CircularProgress size={16} /> : <SearchIcon />}
              </IconButton>
            )
          }}
        />
      </Box>

      {/* Resultados */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {error && (
          <Alert severity="error" sx={{ m: 1 }}>
            {error}
          </Alert>
        )}

        {results.length === 0 && !loading && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Nenhum resultado encontrado' : 'Digite um termo para buscar jurisprudência'}
            </Typography>
          </Box>
        )}

        <List dense>
          {results.map((result) => (
            <ListItem
              key={result.id}
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                mb: 1,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                mb: 1
              }}>
                <Chip
                  label={result.tribunal}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Tooltip title="Inserir no documento">
                  <IconButton
                    size="small"
                    onClick={() => insertJurisprudence(result)}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {result.titulo}
              </Typography>
              
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Processo: {result.numero_processo}
              </Typography>
              
              <Typography variant="body2" sx={{ 
                fontSize: '0.85rem',
                lineHeight: 1.4,
                color: 'text.secondary'
              }}>
                {result.ementa.length > 200 
                  ? result.ementa.substring(0, 200) + '...'
                  : result.ementa
                }
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
  );
};

export default SimplifiedJurisprudencePanel;
